/**
 * Searchandising Management Page
 * Configure boost/bury/pin/exclude rules for search results
 */

import { useState, useMemo } from 'react'
import {
  Plus,
  Trash2,
  Pencil,
  TrendingUp,
  TrendingDown,
  Pin,
  EyeOff,
  Filter,
  Eye,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  useSearchandisingRules,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  usePreviewRule,
  formatRuleType,
  getRuleTypeColor,
  parseRuleArray,
  type SearchandisingRule,
  type RuleType,
  type QueryMatchType,
  type CreateRuleInput,
} from '../hooks/useSearchandising'

// Rule type icons
const RULE_TYPE_ICONS: Record<RuleType, React.ReactNode> = {
  boost: <TrendingUp className="h-4 w-4" />,
  bury: <TrendingDown className="h-4 w-4" />,
  pin: <Pin className="h-4 w-4" />,
  exclude: <EyeOff className="h-4 w-4" />,
}

export default function SearchandisingPage() {
  const { data: rules, isLoading } = useSearchandisingRules()
  const createMutation = useCreateRule()
  const updateMutation = useUpdateRule()
  const deleteMutation = useDeleteRule()
  const previewMutation = usePreviewRule()

  // State
  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | RuleType>('all')
  const [previewOpen, setPreviewOpen] = useState(false)

  // Form state
  const [formData, setFormData] = useState<CreateRuleInput>({
    name: '',
    type: 'boost',
    queryMatch: '',
    queryMatchType: 'contains',
    productIds: [],
    categoryIds: [],
    tagMatch: '',
    priceMin: undefined,
    priceMax: undefined,
    onSale: undefined,
    inStock: undefined,
    boostAmount: 100,
    position: undefined,
    startDate: undefined,
    endDate: undefined,
    enabled: true,
    priority: 0,
  })

  // Filter rules
  const filteredRules = useMemo(() => {
    if (!rules) return []
    if (filter === 'all') return rules
    return rules.filter((r) => r.type === filter)
  }, [rules, filter])

  // Stats
  const stats = useMemo(() => {
    if (!rules) return { total: 0, enabled: 0, boost: 0, bury: 0, pin: 0, exclude: 0 }
    return {
      total: rules.length,
      enabled: rules.filter((r) => r.enabled === 1).length,
      boost: rules.filter((r) => r.type === 'boost').length,
      bury: rules.filter((r) => r.type === 'bury').length,
      pin: rules.filter((r) => r.type === 'pin').length,
      exclude: rules.filter((r) => r.type === 'exclude').length,
    }
  }, [rules])

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'boost',
      queryMatch: '',
      queryMatchType: 'contains',
      productIds: [],
      categoryIds: [],
      tagMatch: '',
      priceMin: undefined,
      priceMax: undefined,
      onSale: undefined,
      inStock: undefined,
      boostAmount: 100,
      position: undefined,
      startDate: undefined,
      endDate: undefined,
      enabled: true,
      priority: 0,
    })
  }

  const handleEdit = (rule: SearchandisingRule) => {
    setEditingId(rule.id)
    setFormData({
      name: rule.name,
      type: rule.type as RuleType,
      queryMatch: rule.queryMatch || '',
      queryMatchType: (rule.queryMatchType as QueryMatchType) || 'contains',
      productIds: parseRuleArray(rule.productIds),
      categoryIds: parseRuleArray(rule.categoryIds),
      tagMatch: rule.tagMatch || '',
      priceMin: rule.priceMin ?? undefined,
      priceMax: rule.priceMax ?? undefined,
      onSale: rule.onSale === 1 ? true : rule.onSale === 0 ? false : undefined,
      inStock: rule.inStock === 1 ? true : rule.inStock === 0 ? false : undefined,
      boostAmount: rule.boostAmount ?? 100,
      position: rule.position ?? undefined,
      startDate: rule.startDate ?? undefined,
      endDate: rule.endDate ?? undefined,
      enabled: rule.enabled === 1,
      priority: rule.priority ?? 0,
    })
    setCreateOpen(true)
  }

  const handleSave = async () => {
    try {
      // Clean up empty values
      const input = { ...formData }
      if (!input.queryMatch) input.queryMatch = undefined
      if (!input.tagMatch) input.tagMatch = undefined
      if (input.productIds?.length === 0) input.productIds = undefined
      if (input.categoryIds?.length === 0) input.categoryIds = undefined

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, ...input })
      } else {
        await createMutation.mutateAsync(input)
      }
      setCreateOpen(false)
      setEditingId(null)
      resetForm()
    } catch {
      // Error handled by mutation
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await deleteMutation.mutateAsync(deleteId)
      setDeleteId(null)
    } catch {
      // Error handled by mutation
    }
  }

  const handleToggleEnabled = async (rule: SearchandisingRule) => {
    try {
      await updateMutation.mutateAsync({
        id: rule.id,
        enabled: rule.enabled !== 1,
      })
    } catch {
      // Error handled by mutation
    }
  }

  const handlePreview = async () => {
    try {
      await previewMutation.mutateAsync(formData)
      setPreviewOpen(true)
    } catch {
      // Error handled by mutation
    }
  }

  // Format timestamp
  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '-'
    return new Date(timestamp * 1000).toLocaleDateString()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Searchandising</h1>
          <p className="text-muted-foreground">
            Control search result ordering with boost, bury, pin, and exclude rules
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Rule
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Rules</CardDescription>
            <CardTitle className="text-2xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active</CardDescription>
            <CardTitle className="text-2xl text-green-600">{stats.enabled}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Boost</CardDescription>
            <CardTitle className="text-2xl">{stats.boost}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Bury</CardDescription>
            <CardTitle className="text-2xl">{stats.bury}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pin / Exclude</CardDescription>
            <CardTitle className="text-2xl">
              {stats.pin} / {stats.exclude}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
        <TabsList>
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
          <TabsTrigger value="boost">Boost ({stats.boost})</TabsTrigger>
          <TabsTrigger value="bury">Bury ({stats.bury})</TabsTrigger>
          <TabsTrigger value="pin">Pin ({stats.pin})</TabsTrigger>
          <TabsTrigger value="exclude">Exclude ({stats.exclude})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Rules Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Filter className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No rules yet</h3>
              <p className="mb-4 text-muted-foreground">
                Create your first searchandising rule to control search result ordering
              </p>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Rule
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rule</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Query Target</TableHead>
                  <TableHead>Product Filter</TableHead>
                  <TableHead>Hits</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{rule.name}</div>
                        {rule.priority !== null && rule.priority > 0 && (
                          <span className="text-xs text-muted-foreground">
                            Priority: {rule.priority}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRuleTypeColor(rule.type as RuleType)}>
                        {RULE_TYPE_ICONS[rule.type as RuleType]}
                        <span className="ml-1">{formatRuleType(rule.type as RuleType)}</span>
                      </Badge>
                      {rule.type === 'boost' || rule.type === 'bury' ? (
                        <span className="ml-2 text-sm text-muted-foreground">
                          {rule.boostAmount && rule.boostAmount > 0 ? '+' : ''}
                          {rule.boostAmount}
                        </span>
                      ) : rule.type === 'pin' && rule.position !== null ? (
                        <span className="ml-2 text-sm text-muted-foreground">
                          #{rule.position + 1}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {rule.queryMatchType === 'all' || !rule.queryMatch ? (
                        <span className="text-muted-foreground">All queries</span>
                      ) : (
                        <div>
                          <code className="rounded bg-muted px-1 text-sm">
                            {rule.queryMatch}
                          </code>
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({rule.queryMatchType})
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {rule.tagMatch && (
                          <Badge variant="outline">Tag: {rule.tagMatch}</Badge>
                        )}
                        {rule.onSale === 1 && <Badge variant="outline">On Sale</Badge>}
                        {rule.inStock === 1 && <Badge variant="outline">In Stock</Badge>}
                        {rule.inStock === 0 && <Badge variant="outline">Out of Stock</Badge>}
                        {(rule.priceMin !== null || rule.priceMax !== null) && (
                          <Badge variant="outline">
                            ${rule.priceMin ?? '0'} - ${rule.priceMax ?? '...'}
                          </Badge>
                        )}
                        {parseRuleArray(rule.categoryIds).length > 0 && (
                          <Badge variant="outline">
                            {parseRuleArray(rule.categoryIds).length} categories
                          </Badge>
                        )}
                        {parseRuleArray(rule.productIds).length > 0 && (
                          <Badge variant="outline">
                            {parseRuleArray(rule.productIds).length} products
                          </Badge>
                        )}
                        {!rule.tagMatch &&
                          rule.onSale === null &&
                          rule.inStock === null &&
                          rule.priceMin === null &&
                          rule.priceMax === null &&
                          parseRuleArray(rule.categoryIds).length === 0 &&
                          parseRuleArray(rule.productIds).length === 0 && (
                            <span className="text-muted-foreground">All products</span>
                          )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {rule.hitCount || 0}
                        {rule.lastHitAt && (
                          <div className="text-xs text-muted-foreground">
                            Last: {formatDate(rule.lastHitAt)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rule.enabled === 1}
                        onCheckedChange={() => handleToggleEnabled(rule)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(rule)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(rule.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) {
            setEditingId(null)
            resetForm()
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Rule' : 'Create Searchandising Rule'}
            </DialogTitle>
            <DialogDescription>
              Configure how search results are modified when this rule matches
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-medium">Basic Information</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Rule Name *</Label>
                  <Input
                    placeholder="e.g., Boost Sale Items"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rule Type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(v) =>
                      setFormData({ ...formData, type: v as RuleType })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boost">Boost (move up)</SelectItem>
                      <SelectItem value="bury">Bury (move down)</SelectItem>
                      <SelectItem value="pin">Pin to Position</SelectItem>
                      <SelectItem value="exclude">Exclude from Results</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Boost/Bury amount or Pin position */}
              {(formData.type === 'boost' || formData.type === 'bury') && (
                <div className="space-y-2">
                  <Label>
                    {formData.type === 'boost' ? 'Boost' : 'Bury'} Amount
                  </Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={formData.boostAmount || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        boostAmount:
                          formData.type === 'bury'
                            ? -Math.abs(parseInt(e.target.value) || 0)
                            : Math.abs(parseInt(e.target.value) || 0),
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Higher values = stronger effect on result ordering
                  </p>
                </div>
              )}

              {formData.type === 'pin' && (
                <div className="space-y-2">
                  <Label>Pin Position (0 = first)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.position ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        position: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              )}
            </div>

            {/* Query Targeting */}
            <div className="space-y-4">
              <h4 className="font-medium">Query Targeting</h4>
              <p className="text-sm text-muted-foreground">
                When should this rule apply?
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Match Type</Label>
                  <Select
                    value={formData.queryMatchType}
                    onValueChange={(v) =>
                      setFormData({
                        ...formData,
                        queryMatchType: v as QueryMatchType,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Queries</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="exact">Exact Match</SelectItem>
                      <SelectItem value="regex">Regular Expression</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.queryMatchType !== 'all' && (
                  <div className="space-y-2">
                    <Label>Query Pattern</Label>
                    <Input
                      placeholder={
                        formData.queryMatchType === 'regex'
                          ? 'e.g., sale|discount'
                          : 'e.g., sale'
                      }
                      value={formData.queryMatch}
                      onChange={(e) =>
                        setFormData({ ...formData, queryMatch: e.target.value })
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Product Selection */}
            <div className="space-y-4">
              <h4 className="font-medium">Product Selection</h4>
              <p className="text-sm text-muted-foreground">
                Which products should this rule affect?
              </p>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tag Match</Label>
                  <Input
                    placeholder="e.g., clearance"
                    value={formData.tagMatch}
                    onChange={(e) =>
                      setFormData({ ...formData, tagMatch: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price Range</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={formData.priceMin ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priceMin: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={formData.priceMax ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priceMax: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Label>On Sale Only</Label>
                  <Select
                    value={
                      formData.onSale === true
                        ? 'yes'
                        : formData.onSale === false
                        ? 'no'
                        : 'any'
                    }
                    onValueChange={(v) =>
                      setFormData({
                        ...formData,
                        onSale:
                          v === 'yes' ? true : v === 'no' ? false : undefined,
                      })
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label>In Stock Only</Label>
                  <Select
                    value={
                      formData.inStock === true
                        ? 'yes'
                        : formData.inStock === false
                        ? 'no'
                        : 'any'
                    }
                    onValueChange={(v) =>
                      setFormData({
                        ...formData,
                        inStock:
                          v === 'yes' ? true : v === 'no' ? false : undefined,
                      })
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Scheduling */}
            <div className="space-y-4">
              <h4 className="font-medium">Scheduling (Optional)</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={
                      formData.startDate
                        ? new Date(formData.startDate * 1000)
                            .toISOString()
                            .split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        startDate: e.target.value
                          ? Math.floor(new Date(e.target.value).getTime() / 1000)
                          : undefined,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={
                      formData.endDate
                        ? new Date(formData.endDate * 1000)
                            .toISOString()
                            .split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        endDate: e.target.value
                          ? Math.floor(new Date(e.target.value).getTime() / 1000)
                          : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Priority */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label>Enabled</Label>
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, enabled: checked })
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <Label>Priority</Label>
                <Input
                  type="number"
                  className="w-20"
                  value={formData.priority ?? 0}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priority: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handlePreview} disabled={!formData.name}>
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                !formData.name ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : editingId
                ? 'Update Rule'
                : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rule Preview</DialogTitle>
            <DialogDescription>
              Products that would be affected by this rule
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {previewMutation.isPending ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : previewMutation.data ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Total products affected: {previewMutation.data.totalAffected}
                </p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {previewMutation.data.preview.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 rounded border p-2"
                    >
                      {p.imageUrl && (
                        <img
                          src={p.imageUrl}
                          alt={p.title}
                          className="h-10 w-10 rounded object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-medium">{p.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {p.category} {p.price && `- $${p.price}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground">
                Click Preview to see affected products
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this searchandising rule. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
